import React from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useForm} from '../hooks/useForm';
import {
    TextField,
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    IconButton,
    CardActions,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Paper
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../components/Loader';
import { EmptyGuid } from '../utils/constants';

export default function ClientForm() {
    const {id} = useParams();
    const navigate = useNavigate();
    const fetchUrl = id ? `/api/client/${id}` : null;
    const saveUrl = `/api/client/`;


    const validate = (data) => {
        const errors = {};
        if (!data.fullName) errors.fullName = 'Full Name is required';
        if (!data.email) errors.email = 'Email is required';
        return errors;
    };

    const {
        data,
        isLoading,
        error,
        validationErrors,
        handleInputChange,
        handleSubmit,
        isNew,
        close
    } = useForm({id, fetchUrl, saveUrl, validate});

    // Compose WhatsApp link based on the phone number
    const getWhatsAppUrl = (phone) =>
        `https://wa.me/${phone.replace(/\s+/g, '') || ''}`;
    const handleAddPet = () => {
        // Navigate to PetForm with empty GUID and client id as query param
        navigate(`/pets/${EmptyGuid}?clientId=${id}`);
    };
    const handleCreateVisit = (pet) => {
        // Navigate to VisitForm with EmptyGuid and petId as query param
        navigate(`/visits/${EmptyGuid}?petId=${pet.id}`);
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{mx: 'auto'}}>
            {/* Header */}
            <Box
                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}
            >
                <Typography variant="h5">
                    {isNew ? 'Cliente nuevo' : 'Editar Cliente'}
                </Typography>
                <IconButton variant="text" onClick={close}>
                    <CloseIcon fontSize="large"/>
                </IconButton>
            </Box>
            <Card sx={{padding: 2}}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant={'standard'}
                                fullWidth
                                label="Nombre"
                                name="fullName"
                                value={data.fullName}
                                onChange={handleInputChange}
                                error={!!validationErrors.fullName}
                                helperText={validationErrors.fullName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant={'standard'}
                                fullWidth
                                label="Correo electrónico"
                                name="email"
                                value={data.email}
                                onChange={handleInputChange}
                                error={!!validationErrors.email}
                                helperText={validationErrors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{display: 'flex'}}>

                            <TextField
                                variant={'standard'}
                                fullWidth
                                label="Teléfono (WhatsApp)"
                                name="phone"
                                value={data.phone}
                                onChange={handleInputChange}
                                error={!!validationErrors.phone}
                                helperText={validationErrors.phone}
                                // For advanced phone masking, consider using a library like react-input-mask.
                            />

                            <IconButton
                                component="a"
                                href={getWhatsAppUrl(data.phone)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsAppIcon/>
                            </IconButton>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant={'standard'}
                                fullWidth
                                label="Teléfono"
                                name="secondPhone"
                                value={data.secondPhone}
                                onChange={handleInputChange}
                                error={!!validationErrors.secondPhone}
                                helperText={validationErrors.secondPhone}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant={'standard'}
                                fullWidth
                                label="Dirección"
                                name="address"
                                value={data.address}
                                onChange={handleInputChange}
                                error={!!validationErrors.address}
                                helperText={validationErrors.address}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Última actualización: {new Date(data.updated).toLocaleString()}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions>
                    <Button type="submit" variant="contained" color="primary">
                        Guardar
                    </Button>
                </CardActions>
            </Card>

            {/* Pets section */}
            <Box sx={{mt: 4}}>
                <Box sx={{display: 'flex', mb: 2, alignItems: 'center'}}>
                    <Typography variant="h6">
                        Mascotas
                    </Typography>
                    <IconButton
                        color="primary"
                        onClick={handleAddPet}
                        disabled={isNew}
                    >
                        <AddIcon/>
                    </IconButton>
                    {isNew && (
                        <Typography variant="caption" color="textSecondary" sx={{ml: 2}}>
                            Guarda primero para agregar mascotas
                        </Typography>
                    )}
                </Box>
                <TableContainer component={Paper}>
                    <Table size="small" hover="true">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Especie</TableCell>
                                <TableCell>Género</TableCell>
                                <TableCell>Color</TableCell>
                                <TableCell>Raza</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.pets.map((pet) => (
                                <TableRow
                                    key={pet.id}
                                    onClick={() => navigate(`/pets/${pet.id}`)}
                                    hover
                                    style={{cursor: 'pointer'}}
                                >
                                    <TableCell>{pet.name}</TableCell>
                                    <TableCell>{pet.type}</TableCell>
                                    <TableCell>{pet.gender}</TableCell>
                                    <TableCell>{pet.color}</TableCell>
                                    <TableCell>{pet.breed}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Crear una visita" arrow>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCreateVisit(pet);
                                                }}
                                            >
                                                <PostAddIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}
